import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Anchor,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  Overlay,
  Paper,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { Brand, Product, User } from "../../../../gql/__generated__/graphql";
import { CREATE_COLLAB } from "../../../apis/lambda/GraphQL/Collab/mutations";
import { GET_ALL_PRODUCTS_FOR_BRAND } from "../../../apis/lambda/GraphQL/Product/queries";
import { userInfoAtom } from "../../../atoms";
import BudgetCalculator from "../../../components/BudgetCalculator/BudgetCalculator";
import ProductSelector from "../../../components/ProductSelector/ProductSelector";

export interface BrandCollabFormValues {
  products: string[];
  videosDesired: number;
  suggestedBudget?: number;
  actualBudget?: number;
  message?: string;
}

export interface CreatorCollabFormValues {
  products: string[];
  message: string;
  videosDesired: number;
  suggestedBudget?: number;
  actualBudget?: number;
}

export interface CollabModalProps {
  opened: boolean;
  onClose: () => void;
  isBrand: boolean; // true for brand, false for creator
  products?: Product[]; // Only needed for brand mode
  brand?: Brand; // Only needed for creator mode
  creator?: User; // Only needed for creator mode
  onSubmit: (values: BrandCollabFormValues | CreatorCollabFormValues) => void;
}

function CollabModal({
  opened,
  onClose,
  isBrand,
  creator,
  brand,
  onSubmit,
}: CollabModalProps) {
  // React State
  const [suggestedBudget, setSuggestedBudget] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [collabSent, setCollabSent] = useState(false);

  // Recoil State
  const userInfo = useRecoilValue(userInfoAtom);

  // GraphQL
  const [getAllProducts, allProductsResponse] = useLazyQuery(
    GET_ALL_PRODUCTS_FOR_BRAND,
    {
      onError: (error) => {
        console.error("Error fetching products:", error);
      },
    }
  );
  // Mutations
  const [createCollab] = useMutation(CREATE_COLLAB);

  useEffect(() => {
    if (opened) {
      if (isBrand && userInfo?.brandId) {
        void getAllProducts({ variables: { brandId: userInfo.brandId } });
      } else if (!isBrand && brand?.id) {
        void getAllProducts({ variables: { brandId: brand.id } });
      }
    }
  }, [opened, isBrand, brand?.id, userInfo?.brandId, getAllProducts]);

  // Reset collab sent state when modal opens
  useEffect(() => {
    if (opened) {
      setCollabSent(false);
      setSuggestedBudget(null);
    }
  }, [opened]);

  const commonFormValidation = {
    products: (value: string[]) =>
      value.length === 0
        ? "Select at least one product"
        : value.length > 5
        ? "Maximum 5 products allowed"
        : null,
    videosDesired: (value: number) => {
      if (value < 1) return "Must request at least 1 video";
      if (value > 100) return "Maximum 100 videos";
      return null;
    },
    actualBudget: (value: number | undefined) => {
      if (value === undefined || value === null) return "Budget is required";
      if (value <= 0) return "Budget must be greater than 0";
      if (suggestedBudget && value < suggestedBudget * 0.8)
        return "Budget is too low compared to suggestion";
      return null;
    },
  };

  // Form for brands
  const brandForm = useForm<BrandCollabFormValues>({
    initialValues: {
      products: [],
      videosDesired: 5,
      suggestedBudget: undefined,
      actualBudget: undefined,
      message: "",
    },
    validate: commonFormValidation,
  });

  // Form for creators
  const creatorForm = useForm<CreatorCollabFormValues>({
    initialValues: {
      products: [],
      message: "",
      videosDesired: 1,
      suggestedBudget: undefined,
      actualBudget: undefined,
    },
    validate: {
      ...commonFormValidation,
      message: (value) =>
        value.trim().length < 10
          ? "Message must be at least 10 characters"
          : null,
    },
  });

  const handleBudgetCalculated = (calculatedBudget: number) => {
    setSuggestedBudget(calculatedBudget);
    const form = isBrand ? brandForm : creatorForm;
    form.setFieldValue("suggestedBudget", calculatedBudget);
    form.setFieldValue("actualBudget", calculatedBudget);
  };

  const handleBrandSubmit = async (values: BrandCollabFormValues) => {
    setIsSubmitting(true);
    try {
      if (creator && values.actualBudget) {
        await createCollab({
          variables: {
            input: {
              brandId: userInfo?.brandId!,
              creatorEmail: creator.email,
              numVideosDesired: values.videosDesired,
              productIds: values.products,
              price: values.actualBudget ?? values.suggestedBudget,
              message: values.message,
            },
          },
        });
        setCollabSent(true);
        onSubmit(values);
      }
    } catch (error) {
      console.error("Error creating collaboration:", error);
      notifications.show({
        title: "Error",
        message: "Failed to send collab offer. Please try again.",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreatorSubmit = async (values: CreatorCollabFormValues) => {
    setIsSubmitting(true);
    try {
      if (brand && values.actualBudget) {
        await createCollab({
          variables: {
            input: {
              brandId: brand.id,
              creatorEmail: userInfo?.email!,
              numVideosDesired: values.videosDesired,
              productIds: values.products,
              price: values.actualBudget ?? values.suggestedBudget,
              message: values.message,
            },
          },
        });
        setCollabSent(true);
        onSubmit(values);
      }
    } catch (error) {
      console.error("Error creating collaboration:", error);
      notifications.show({
        title: "Error",
        message: "Failed to send collab offer. Please try again.",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const form = isBrand ? brandForm : creatorForm;
  const handleSubmit = isBrand
    ? brandForm.onSubmit(handleBrandSubmit)
    : creatorForm.onSubmit(handleCreatorSubmit);

  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {allProductsResponse.loading && <Loader />}
        {allProductsResponse.error && (
          <Text color="red">Error fetching products</Text>
        )}
        {allProductsResponse.data?.getAllProductsForBrand.length ? (
          <ProductSelector
            products={allProductsResponse.data.getAllProductsForBrand}
            selectedProductIds={form.values.products}
            onChange={(selectedIds) =>
              form.setFieldValue("products", selectedIds)
            }
          />
        ) : (
          <Text>
            {isBrand
              ? "No products found. Please add some products before making Collab offers."
              : "This brand has not added any products yet."}
          </Text>
        )}

        <NumberInput
          label={isBrand ? "Videos Desired" : "Number of Videos"}
          description={
            isBrand
              ? "Number of videos you want the creator to make"
              : "How many videos you'd like to create for this brand."
          }
          placeholder="Enter number of videos"
          min={1}
          max={100}
          required
          {...form.getInputProps("videosDesired")}
        />

        {((isBrand && creator) || (!isBrand && brand)) &&
          allProductsResponse.data?.getAllProductsForBrand && (
            <BudgetCalculator
              mode={"brand"}
              email={isBrand && creator ? creator.email : userInfo?.email!}
              videosDesired={form.values.videosDesired}
              products={allProductsResponse.data.getAllProductsForBrand.filter(
                (p) => form.values.products?.includes(p.id)
              )}
              isValid={!form.errors.products && !form.errors.videosDesired}
              onBudgetCalculated={handleBudgetCalculated}
            />
          )}

        {suggestedBudget !== null && (
          <Stack gap={"sm"}>
            <NumberInput
              label={`Your Collaboration Budget ($) sent to the ${
                isBrand ? "creator" : "brand"
              }`}
              description="You can adjust this, but we recommend staying close to the suggestion"
              placeholder="Enter your budget"
              min={1}
              required
              {...form.getInputProps("actualBudget")}
            />
            <Textarea
              label={
                isBrand
                  ? "Additional Message (Optional)"
                  : `Message to ${brand?.name}`
              }
              description={
                isBrand
                  ? "Add any specific details or requirements for the creator"
                  : "Introduce yourself and explain why you'd like to collaborate"
              }
              placeholder="Enter your message"
              minRows={isBrand ? 3 : 5}
              required={!isBrand}
              {...form.getInputProps("message")}
            />
          </Stack>
        )}

        <Group mt="xl" justify="flex-end">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              !form.isValid() ||
              suggestedBudget === null ||
              isSubmitting ||
              collabSent
            }
            loading={isSubmitting}
            color="primary"
          >
            Send Collaboration Proposal
          </Button>
        </Group>
      </Stack>
    </form>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        isBrand
          ? `Propose Collaboration to ${creator?.name || "the Creator"}`
          : `Propose Collaboration to ${brand?.name}`
      }
      size="xl"
      centered
    >
      <div style={{ position: "relative" }}>
        {renderForm()}

        {collabSent && (
          <Overlay backgroundOpacity={0.35} blur={10} zIndex={1000}>
            <Center h="100%">
              <Paper p="xl" radius="md" shadow="lg" ta="center" maw={400}>
                <Stack align="center" gap="md">
                  <IconCheck size={48} color="secondary" />
                  <Text size="xl" fw={600} c="secondary">
                    Success!
                  </Text>
                  <Text size="md">
                    Your Collab has been sent to{" "}
                    {isBrand ? creator?.name || "the creator" : brand?.name}!
                  </Text>
                  <Anchor
                    component={Link}
                    to="/collabs"
                    reloadDocument
                    onClick={onClose}
                    size="lg"
                    fw={500}
                  >
                    Click here to view your collabs
                  </Anchor>

                  <Button variant="outline" onClick={onClose} mt="md">
                    Close
                  </Button>
                </Stack>
              </Paper>
            </Center>
          </Overlay>
        )}
      </div>
    </Modal>
  );
}

export default CollabModal;
